import React, { ComponentProps, FC } from "react";
import { Formik, Form, FieldArray, useFormikContext, FormikConfig } from "formik";
import * as _ from "lodash";

import { Space, Row, Col, Button, DeleteOutlined, PlusOutlined } from "@ctra/components";
import { ChartType, OverlayViews, DataDescriptorEntity, MetricEntity } from "@ctra/api";

import { useDataDictionary } from "@base";
import { BaseIntf, ChartSelector } from "@analytics";

import { MetricPicker } from "../MetricPicker";
import styles from "./OverlayManager.module.less";
import { useDeepCompareEffect } from "use-deep-compare";

interface FormValues {
  items: Array<{
    metricID: ComponentProps<typeof ChartSelector>["metricID"];
    variantID: ComponentProps<typeof ChartSelector>["variantID"];
  }>;
}

/**
 * Form wrapper for managing overlays
 * @param {(values: FormValues) => void} onChange
 * @param {Array<ChartType> | undefined} enabledViews
 * @param {ChartType | undefined} currentView
 * @param {Omit<React.PropsWithChildren<FormikConfig<FormValues> & {onChange: (values: FormValues) => void, enabledViews?: Array<ChartType>, currentView?: ChartType}>, "currentView" | "enabledViews" | "onChange">} props
 * @returns {JSX.Element}
 * @constructor
 */
export const OverlayManager: FC<
  FormikConfig<FormValues> & {
    onChange: (values: FormValues) => void;
    enabledViews?: Array<ChartType>;
    currentView?: ChartType;
    onRemove?: () => void;
  }
> = ({ onChange, enabledViews, currentView, onRemove, ...props }) => (
  <Formik<FormValues> {...props}>
    {() => (
      <FormComponent
        onChange={onChange}
        onRemove={onRemove}
        enabledViews={enabledViews}
        currentView={currentView}
      />
    )}
  </Formik>
);

/**
 * Form component for managing overlays
 * @param {(values: FormValues) => void} onChange
 * @param {Array<ChartType> | undefined} enabledViews
 * @param {ChartType | undefined} currentView
 * @returns {JSX.Element}
 * @constructor
 */
const FormComponent: FC<{
  onChange: (values: FormValues) => void;
  enabledViews?: Array<ChartType>;
  currentView?: ChartType;
  onRemove?: () => void;
}> = ({ onChange, onRemove, enabledViews, currentView }) => {
  const { values, setFieldValue } = useFormikContext<FormValues>();

  const {
    dataDescriptors,
    metrics,
    api: { hasViewSupport }
  } = useDataDictionary();

  /**
   * Use restricted views when the user adds more than one item: only the ons which can be mixed in overlays
   * @type {Array<ChartType> | undefined}
   */
  const restrictedViews =
    _.size(values.items) > 1 ? _.intersection(OverlayViews, enabledViews) : enabledViews;

  /**
   * Hard reset the stack to the main chart when the user still manages to pick an unsupported view
   */
  useDeepCompareEffect(() => {
    if (!!currentView && !!_.size(restrictedViews) && !_.includes(restrictedViews, currentView)) {
      const [main] = values.items;

      if (main) {
        setFieldValue("items", [main]);
      }
    }
  }, [currentView, restrictedViews]);

  /**
   * Handle change for the chart selector
   * @param {number} idx
   * @param {Array<BaseIntf>} blacklist
   * @param {[metricID: MetricEntity["id"], variantID?: DataDescriptorEntity["id"]]} v
   */
  const handleChange = (
    idx: number,
    blacklist: Array<BaseIntf>,
    ...v: [metricID: MetricEntity["id"], variantID?: DataDescriptorEntity["id"]]
  ) => {
    const [metricID, variantID] = v;
    const excludedVariants = _.map(blacklist, "variantID");
    const metricEntity = _.get(metrics, [metricID]);

    /**
     * Find the default variant ID for the metric which supports the enabled views
     */
    const defaultVariantID = _.find(
      _.without(_.get(metricEntity, ["variants"]), ...excludedVariants),
      (variantID) => {
        const variant = dataDescriptors[variantID];

        return variant && hasViewSupport(variant, restrictedViews);
      }
    );

    /**
     * Field value to be set
     * @type {{metricID: string, variantID: any}}
     */
    const fieldValue = {
      metricID,
      variantID: _.defaultTo(variantID, defaultVariantID)
    };

    setFieldValue(`items.${idx}`, fieldValue);
    onChange(_.set({ ...values }, ["items", idx], fieldValue));
  };

  return (
    <Form className={styles.Form}>
      <FieldArray name="items">
        {({ remove, push }) => {
          const { items } = values;

          /**
           * Trick to always show an empty metric picker when we run out of items to display
           * @type {Array<{metricID: React.ComponentProps<typeof ChartSelector>["metricID"], variantID: React.ComponentProps<typeof ChartSelector>["variantID"]}>}
           */
          const withDefault = _.size(items) ? items : [{} as typeof items[number]];

          return _.map(withDefault, (item, idx, collection) => {
            /**
             * Tell the size of the field array
             * @type {number}
             */
            const size = _.size(collection);

            /**
             * Tell if the item may be deleted
             * @type {boolean}
             */
            const mayDelete = !!idx && size > 1;

            /**
             * Tell if the whole widget may be removed
             * @type {value is (...args: any[]) => any}
             */
            const mayRemove = size === 1 && _.isFunction(onRemove);

            /**
             * Tell if a new item may be pushed (when there is no empty item in the collection)
             * @type {boolean}
             */
            const mayPushNew =
              (!currentView || _.includes(OverlayViews, currentView)) &&
              idx === size - 1 &&
              _.every(collection, "metricID");

            /**
             * Get all the previous items in the collection
             * @type {{metric: React.ComponentProps<typeof ChartSelector>["metricID"], variant: React.ComponentProps<typeof ChartSelector>["variant"]}[]}
             */
            const blacklist = _.slice(collection, 0, idx);

            return (
              <Row gutter={8} wrap={false} align="middle" key={idx} className={styles.Row}>
                <Col flex={1}>
                  {item.metricID ? (
                    <ChartSelector
                      disabled={idx < size - 1}
                      metricID={item.metricID}
                      variantID={item.variantID}
                      blacklist={blacklist}
                      onChange={_.partial(handleChange, idx, blacklist)}
                      isOptionDisabled={(entity) => {
                        return size <= 1 ? false : !hasViewSupport(entity, restrictedViews);
                      }}
                      className={styles.ChartSelector}
                    />
                  ) : (
                    <MetricPicker
                      isOptionDisabled={(entity) => {
                        return size <= 1 ? false : !hasViewSupport(entity, restrictedViews);
                      }}
                      blacklist={blacklist}
                      onChange={_.partial(handleChange, idx, blacklist)}
                    />
                  )}
                </Col>
                <Col>
                  <Space size={8}>
                    <Button
                      size="large"
                      disabled={!(mayRemove || mayDelete)}
                      icon={<DeleteOutlined />}
                      onClick={() => {
                        if (mayDelete) {
                          const { items } = values;

                          remove(idx);

                          /**
                           * Only notify the parent if we remove
                           * an already selected metric and not an empty one
                           */
                          if (_.every(items, "metricID")) {
                            onChange({
                              ...values,
                              items: items.filter((_, i) => i !== idx)
                            });
                          }
                        } else if (mayRemove) {
                          onRemove();
                        }
                      }}
                    />
                    <Button
                      disabled={!mayPushNew}
                      size="large"
                      icon={<PlusOutlined />}
                      onClick={_.partial(_.unary(push), {})}
                    />
                  </Space>
                </Col>
              </Row>
            );
          });
        }}
      </FieldArray>
    </Form>
  );
};
